import _ from '@lodash';
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip as MuiTooltip, TextField, Checkbox, ListItemText, Chip, TableContainer, Table, TableHead, TableBody, TableCell, TableRow, Paper } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';
import format from 'date-fns/format';
import { sub } from 'date-fns';
import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';
import { TbWaveSine } from 'react-icons/tb';
import { PiSteps } from 'react-icons/pi';
import { MdShowChart, MdOutlineTableChart } from 'react-icons/md';
import { FaFilter } from 'react-icons/fa6';

const intervalButtons = [
    { label: 'All', unit: null },
    { label: '1y', unit: { years: 1 } },
    { label: '1m', unit: { months: 1 } },
    { label: '1w', unit: { weeks: 1 } },
    { label: '1d', unit: { days: 1 } }
];

export default function ChartPopup(props) {
    const [data, setData] = useState(props.data);
    const [keys, setKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [lineType, setLineType] = useState('monotone');
    const [isChartView, setIsChartView] = useState(true);

    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const mainTheme = useSelector(selectMainTheme);

    useEffect(() => { setData(props.data); }, [props.data]);

    useEffect(() => {
        console.log('Chart Popup data', data);

        if (data?.length > 0) {
            setMinDate(new Date(data[0].ts));
            setMaxDate(new Date(data[data.length - 1].ts));
            setKeys(extractAllKeys(data));
        }
    }, [data]);

    useEffect(() => {
        if (keys.length > 0 && selectedKeys.length === 0)
            setSelectedKeys([keys[0]]);
    }, [keys]);

    useEffect(() => {
        if (minDate && maxDate) {
            setDateFrom(minDate);
            setDateTo(maxDate);
        }
    }, [minDate, maxDate]);

    useEffect(() => {
        if (data && selectedKeys.length && dateFrom && dateTo) {
            const merged = {};

            selectedKeys.forEach((key) => {
                const rows = getDataByKey(key, data);
                rows.forEach(({ ts, value, outlier, outlierDetails }) => {
                    if (!merged[ts]) merged[ts] = { ts };
                    merged[ts][key] = value;
                    if (outlier) {
                        merged[ts].outlier = true;
                        merged[ts].outlierDetails = outlierDetails;
                    }
                });
            });

            const result = Object.values(merged).filter(({ ts }) => {
                const date = new Date(ts);
                return date >= dateFrom && date <= dateTo;
            });

            setFilteredData(result);
        }
    }, [data, selectedKeys, dateFrom, dateTo]);

    const isDataAvailable = () => keys.length > 0 && minDate && maxDate;

    const extractAllKeys = (array) => {
        const keysSet = new Set();
        array.forEach(item => {
            _.forOwn(item, (value, key) => {
                if (["id", "ts", "meta"].includes(key)) return;
                if (_.isPlainObject(value)) _.forOwn(value, (v, childKey) => keysSet.add(`${key}.${childKey}`));
                else keysSet.add(key);
            });
        });
        return Array.from(keysSet).sort();
    };

    const getDataByKey = (_key, _data) => {
        const result = [];
        const [parentKey, childKey] = _key.split('.');

        _data.forEach(row => {
            let value;
            let outlier = false;
            let outlierDetails = null;

            if (childKey) {
                if (row[parentKey]?.[childKey] !== undefined) value = row[parentKey][childKey];
            } else if (row[_key] !== undefined) {
                value = row[_key];
            }

            if (row.meta?.outlier_detection?.outlier === true) {
                outlier = true;
                outlierDetails = row.meta.outlier_detection.details || null;
            }

            if (value !== undefined) result.push({ ts: row.ts, value, outlier, outlierDetails });
        });

        return result;
    };

    const handleIntervalSelect = (unit) => {
        if (!unit) {
            setDateFrom(minDate);
            return;
        }
        const newFrom = sub(maxDate, unit);
        if (newFrom < minDate) return;
        setDateFrom(newFrom);
    };

    const isIntervalAvailable = (unit) => {
        if (!minDate || !maxDate) return false;
        if (!unit) return true;
        return sub(maxDate, unit) >= minDate;
    };

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Last ' : '') + label} arrow>
                    <span>
                        <IconButton size='small' onClick={() => handleIntervalSelect(unit)} disabled={!isIntervalAvailable(unit)}>
                            <span className='text-xs font-semibold p-4'>{label}</span>
                        </IconButton>
                    </span>
                </MuiTooltip>
            ))}
            <MuiTooltip title='Toggle line type' arrow>
                <span>
                    <IconButton size='small' onClick={() => setLineType(prev => prev === 'monotone' ? 'step' : 'monotone')} disabled={!isChartView}>
                        {lineType === 'monotone' ? <PiSteps size={20} /> : <TbWaveSine size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>
            <MuiTooltip title={isChartView ? 'Table view' : 'Chart view'} arrow>
                <span>
                    <IconButton size='small' onClick={() => setIsChartView(prev => !prev)}>
                        {isChartView ? <MdOutlineTableChart size={20} /> : <MdShowChart size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>
        </div>
    );

    const CustomizedDot = React.memo(({ cx, cy, payload }) => {
        if (payload.outlier) return <circle cx={cx} cy={cy} r={5} fill='red' stroke='white' strokeWidth={1.5} />;
        return null;
    });

    const HoverDot = React.memo(({ cx, cy }) => (
        <circle cx={cx} cy={cy} r={4} fill='#252f3e' stroke='white' strokeWidth={1.5} />
    ));

    const getTooltip = ({ active, payload }) => {
        if (active && payload?.length) {
            const { payload: p } = payload[0];
            const keyLabelDictionary = {
                outlier_detector_name: 'OOD name',
                outlier_detector_version: 'OOD version',
                prediction: 'Prediction'
            };

            return (
                <div className='flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg min-w-[200px] max-w-[300px]'>
                    <div className='mb-2 font-semibold text-[11px] opacity-80'>{format(new Date(p.ts), 'yyyy.MM.dd. HH:mm:ss')}</div>
                    {selectedKeys.map(key => (p[key] !== undefined ? <div key={key} className='font-medium'>{key}: {formatLabelValue(p[key], defaultLabelStyle())}</div> : null))}
                    {p.outlierDetails && (
                        <div className='mt-2 text-[10px] text-gray-300 leading-snug overflow-y-auto max-h-[150px] pr-2'>
                            {p.outlierDetails.map((detail, i) => (
                                <div key={i} className='mb-1'>
                                    {Object.entries(detail).map(([k, v]) => (<div key={k}><b>{keyLabelDictionary[k] || k}:</b> {String(v)}</div>))}
                                    {i < p.outlierDetails.length - 1 && <hr className='my-1 opacity-30' />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open fullWidth maxWidth={filteredData.length ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className={'flex items-center justify-between gap-32 px-32 ' + (filteredData.length ? 'border-b pb-14' : '')}>
                        <div className='whitespace-nowrap'>{props.title || ('Timeseries' + (props.name ? (' data for ' + props.name) : ''))}</div>
                        <Button onClick={() => props.onClose()}>Close</Button>
                    </div>
                    {isDataAvailable() && <div className='w-full flex flex-col gap-8 shadow-inner px-20 pt-14'>
                        <div className='w-full flex flex-col gap-8 rounded-xl bg-grey-100 py-10 shadow px-16'>
                            <Autocomplete
                                className='flex-1'
                                multiple
                                options={keys}
                                value={selectedKeys}
                                onChange={(e, newValue) => setSelectedKeys(newValue)}
                                disableCloseOnSelect
                                getOptionLabel={(option) => option}
                                renderOption={(option, { selected }) => {
                                    const parts = option.split('.');
                                    const parent = parts.length > 1 ? parts[0] : null;
                                    const child = parts.length > 1 ? parts[1] : option;
                                    return (
                                        <>
                                            <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                            <ListItemText primary={parent ? (<span><span className='font-semibold opacity-30'>{parent}.</span>{child}</span>) : (option)} />
                                        </>
                                    );
                                }}
                                renderTags={(value, getTagProps) => value.map((option, index) => (<Chip label={option} {...getTagProps({ index })} size='small' style={{ padding: '5px 5px', marginRight: 4, marginBottom: 4, fontSize: '12px' }} />))}
                                renderInput={(params) => {
                                    const { InputProps: extractedInputProps, inputProps: extractedInnerInputProps, ...filteredParams } = params;

                                    return (
                                        <TextField
                                            {...filteredParams}
                                            variant="outlined"
                                            label="Search and select"
                                            placeholder="Select keys"
                                            InputLabelProps={{ shrink: true }}
                                            // InputProps={{ ...extractedInputProps, style: { minHeight: 40 } }}
                                            // inputProps={{ ...extractedInnerInputProps, style: { padding: '10px 8px', fontSize: '13px', lineHeight: 1.5 } }}
                                            InputProps={{
                                                ...extractedInputProps,
                                                style: { minHeight: 40 },
                                                inputProps: {
                                                    ...extractedInnerInputProps,
                                                    style: {
                                                        padding: '10px 8px',
                                                        fontSize: '13px',
                                                        lineHeight: 1.5
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                }}
                            />
                            <div className='flex-1 flex items-center gap-16'>
                                <DatePick className='flex-1' label='From' value={dateFrom} minDate={minDate} maxDate={maxDate} onChange={(v) => setDateFrom(v)} />
                                <DatePick className='flex-1' label='To' value={dateTo} minDate={maxDate} maxDate={maxDate} onChange={(v) => setDateTo(v)} />
                                <IntervalButtons />
                            </div>
                        </div>
                    </div>}
                </DialogTitle>
                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: filteredData.length ? '520px' : 'auto' }}>
                    {data === undefined && <div className='text-center'>Loading data…</div>}
                    {data === null && <div className='text-center'>No data available</div>}
                    {filteredData.length > 0 && (
                        <div className='w-full flex-1' style={{ minHeight: 400 }}>
                            {isChartView ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                    <LineChart data={filteredData} margin={{ top: 10, left: selectedKeys.length === 0 ? -45 : 10, right: 10, bottom: 10 }}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                        <YAxis tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                        <Tooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />
                                        <Legend layout='horizontal' verticalAlign='bottom' align='center' iconType='square' iconSize={10} wrapperStyle={{ paddingTop: 20 }} />
                                        {selectedKeys.map((key, idx) => (<Line key={key} type={lineType} dataKey={key} name={key} stroke={selectedKeys.length === 1 ? '#252f3e' : `hsl(${idx * 60}, 70%, 40%)`} strokeWidth={2} dot={<CustomizedDot />} activeDot={<HoverDot />} />))}
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                // <div className='w-full h-full bg-red-500'></div>
                                <DataTable data={filteredData} selectedKeys={selectedKeys} />
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}

function DataTable({ data, selectedKeys }) {
    const [q, setQ] = useState('');
    const [outlierOnly, setOutlierOnly] = useState(false);

    const filteredRows = useMemo(() => {
        let result = [...data];
        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            result = result.filter(row => {
                return selectedKeys.some(key =>
                    String(row[key] ?? '').toLowerCase().includes(needle)
                );
            });
        }
        if (outlierOnly) {
            result = result.filter(row => row.outlier === true);
        }
        return result;
    }, [data, selectedKeys, q, outlierOnly]);

    return (
        <TableContainer component={Paper} style={{ height: '100%' }}>
            <Table className="w-full shadow-2xl">
                <TableHead>
                    <TableRow>
                        <TableCell className="text-xs font-semibold">Timestamp</TableCell>
                        {selectedKeys.map(key => (
                            <TableCell key={key} align="right" className="text-xs font-semibold whitespace-nowrap">{key}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredRows.length > 0 ? (
                        filteredRows.map((row, idx) => (
                            <TableRow key={idx} className={`${(idx % 2 === 1 ? 'bg-gray-100' : '')} ${row.outlier ? 'bg-red-50' : ''}`}>
                                <TableCell className="whitespace-nowrap text-xs">{format(new Date(row.ts), 'yyyy.MM.dd.')}</TableCell>
                                {selectedKeys.map(key => (
                                    <TableCell key={key} align="right" className="whitespace-nowrap text-xs tabular-nums">
                                        {row[key] !== undefined ? formatLabelValue(row[key], defaultLabelStyle()) : '-'}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={selectedKeys.length + 1} align="center" className="py-16 opacity-60 text-xs">
                                No data available.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function DatePick(props) {
    return (
        <DatePicker
            className={props.className}
            label={props.label}
            margin='dense'
            value={props.value ?? undefined}
            onChange={e => props.onChange(e)}
            ampm={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            format='yyyy MMMM d.'
            cancelLabel='Vissza'
            okLabel='Ok'
            clearLabel='Törlés'
            inputVariant='outlined'
            clearable
        />
    );
}
